import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScheduling } from '../hooks/useScheduling';
import { SlotPicker } from '../components/SlotPicker';
import { SelectedSlotCard } from '../components/SelectedSlotCard';
import { SuccessScreen } from '../components/SuccessScreen';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { Loader2, CalendarClock, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function Schedule() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    slots,
    clientName,
    isLoadingSlots,
    slotsError,
    alreadyScheduled,
    schedulingInfo,
    selectedSlot,
    setSelectedSlot,
    createBooking,
    isCreatingBooking,
    bookingError,
    bookingSuccess,
    bookingData,
  } = useScheduling(token);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Se o agendamento foi bem-sucedido, mostrar tela de sucesso
  if (bookingSuccess && bookingData?.data) {
    return <SuccessScreen bookingData={bookingData.data} clientName={clientName} />;
  }

  // Se o agendamento já foi processado, mostrar tela de sucesso com flag alreadyScheduled
  if (alreadyScheduled && schedulingInfo) {
    return (
      <SuccessScreen
        clientName={schedulingInfo.clientName}
        scheduledAt={schedulingInfo.scheduledAt}
        alreadyScheduled={true}
      />
    );
  }

  // Validação de token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] dark:bg-[#0f0606] p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Token Inválido
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Este link de agendamento não é válido ou expirou.
                <br />
                Por favor, verifique o link que você recebeu.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Erro ao carregar slots
  if (slotsError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] dark:bg-[#0f0606] p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Erro</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{slotsError}</p>
              <Button onClick={() => window.location.reload()} className="w-full">
                Tentar Novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleConfirmBooking = async () => {
    try {
      await createBooking();
      setShowConfirmDialog(false);
    } catch (error) {
      // Error já está sendo tratado pelo hook; fecha o modal para exibir o erro na tela principal
      setShowConfirmDialog(false);
      console.error('Erro ao criar booking:', error);
    }
  };

  const formatSlotDate = (isoString: string) => {
    return new Date(isoString).toLocaleString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // `items-start` + `sm:items-center`: com `items-center` puro, um card mais
  // alto que a viewport era centralizado e transbordava para CIMA e para
  // baixo — o topo cortado não é alcançável por scroll, e o botão de
  // confirmar no rodapé ficava inacessível em celulares de tela baixa
  // (o SlotPicker cresce conforme a quantidade de horários disponíveis).
  //
  // Alinhando ao topo, o conteúdo sempre começa no início e rola até o fim.
  // A partir de `sm` a tela comporta o card, e o centramento volta.
  return (
    <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#0f0606] flex items-start justify-center sm:items-center p-4 py-6 sm:py-4">
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CalendarClock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Agende seu Onboarding</CardTitle>
          </div>
          {clientName && (
            <CardDescription>
              Olá, <span className="font-semibold text-primary">{clientName}</span>! Escolha o
              melhor horário para você, e lembre-se que todos os horários exibidos estão no fuso
              horário de Brasília (BRT, UTC-3).
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Slots */}
          <div>
            <SlotPicker
              slots={slots}
              selectedSlot={selectedSlot}
              onSelectSlot={setSelectedSlot}
              isLoading={isLoadingSlots}
            />
          </div>

          {/* Selected Slot Card - Sempre visível */}
          <SelectedSlotCard slot={selectedSlot} />

          {/* Booking Error */}
          {bookingError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-200">Erro ao agendar</p>
                  <p className="text-sm text-red-700 dark:text-red-300">{bookingError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Confirm Button */}
          <div className="pt-4">
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={!selectedSlot || isCreatingBooking}
              className={`w-full ${!selectedSlot || isCreatingBooking ? 'cursor-not-allowed' : ''}`}
              size="lg"
            >
              {isCreatingBooking ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Agendando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Confirmar Agendamento
                </>
              )}
            </Button>
          </div>

          {/* Footer info */}
          <p className="text-xs text-center text-gray-600 dark:text-gray-400">
            Você receberá uma confirmação por email e um lembrete via WhatsApp.
          </p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title="Confirmar agendamento"
        description={
          selectedSlot
            ? `Deseja confirmar o agendamento para ${formatSlotDate(selectedSlot.startTime)}? Após a confirmação não será possível alterar o horário.`
            : ''
        }
        confirmText="Confirmar"
        cancelText="Voltar"
        icon="check"
        onConfirm={handleConfirmBooking}
        isLoading={isCreatingBooking}
      />
    </div>
  );
}
